/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  FormEvent,
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import Typography from '@material-ui/core/Typography';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  AssetBulkEditorFormFields
} from '@ion/core/src/types/editors';
import {
  Account,
  Asset,
  Entity,
  GroupTypeEntity
} from '@ion/core/src/types/entities';
import {toISOString} from '@ion/core/src/utils/DateUtil';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {isValue} from '@ion/core/src/utils/StringUtil';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import FormGroupFields from '../components/forms/FormGroupFields';
import Dropdown from '../components/menus/Dropdown';
import Switch from '../components/switch/Switch';
import {useAnimatedMount} from '../hooks/useAnimatedMount';
import {useExtendStyles} from '../hooks/useExtendStyles';
import {useFormState} from '../hooks/useFormState';
import {useMedia} from '../hooks/useMedia';
import {useMergeState} from '../hooks/useMergeState';
import useThemeContext from '../theme/useThemeContext';
import {DatePickerTextConfig} from './datetimepicker/DatePicker';
import DatePicker from './datetimepicker/DatePickerExporter';
import {verifyCommonAttributes} from './AssetBulkEditorUtils';
import {getADMAssetTypes} from './AssetEditor';
import {deserialize, serialize} from './AssetEditorUtils';
import EditorDialog from './EditorDialog';
import styles from './editorStyles';
import EngineTypeChooser from './EngineTypeChooser';
import EntityCategoryChooser from './EntityCategoryChooser';
import EntityStatusChooser from './EntityStatusChooser';
import EntityTypeChooser from './EntityTypeChooser';
import GroupChooser from './GroupChooser';
import IOProfileChooser from './IOProfileChooser';
import MaintenanceScheduleChooser from './MaintenanceScheduleChooser';
import ScheduleChooser from './ScheduleChooser';

// defines the Asset bulk editor prop type definitions ...
interface AssetBulkEditorProps extends DatePickerTextConfig {
  open: boolean;
  assets?: Asset[];
  account: Account;
  clear?: boolean;
  loading?: boolean;
  hasEditStatusAccess?: boolean;
  isMultiDevice?: boolean;
  onSave: (assets: Asset[]) => void;
  onCancel?: (event: SyntheticEvent<{}, Event>) => void;
  isCtcAdmin?: boolean;
}

// defines the text input prop type definitions ...
interface TargetType {
  name: AssetBulkEditorFormFields,
  value: unknown;
  required?: boolean
}

/**
 * @AssetBulkEditor
 * The Asset Bulk edit Form with set fields and encapsulated logic for handling the form's state
 *
 * @param {boolean} open Determines if the AssetBulkEditor dialog should be open
 * @param {object} account User's account object
 * @param {Function} onCancel Callback function fired when the dialog is to be closed.
 * @param {Function} onSave Function the be run when the form is submitted.
 *   The function is passed an asset object
 * @param {Asset[]} assets Assets that passed to the form
 * @param {boolean} clear If true we clear the form state
 * @param {boolean} loading disables any for submission while the form is busy
 */
export const AssetBulkEditor = ({
  open,
  account,
  onSave,
  onCancel,
  validDateFormats,
  assets = [],
  clear = false,
  loading = false,
  isCtcAdmin = false,
  hasEditStatusAccess = true,
  isMultiDevice = false,
  allowManualDateEntry = false
}: AssetBulkEditorProps) => {
  const {isCurrent: isCurrentTheme}           = useThemeContext();
  const editorStyles                          = useExtendStyles((theme) => styles(theme, isCurrentTheme), styles);
  const classes                               = editorStyles();
  const {isPhone}                             = useMedia();
  const {isVisible, onRemove}                 = useAnimatedMount();
  const [formState, setFormState]             = useFormState();
  const [assetDatas, setAssetDatas]           = useState<Asset[]>();
  const [groupsLoading, setGroupsLoading]     = useState(false);
  const [groupValue, setGroupValue]           = useState(null);
  const [openDatePickers, setOpenDatePickers] = useMergeState();
  const [isFormChanged, setIsFormChanged]     = useState(false);
  const entityLabel                           = translate('common:entityLabels.assets');
  
  const accountId = useMemo(() => (
    entityId(groupValue || account)
  ), [account, groupValue]);

  useEffect(() => {
    if (!isObjectEmpty(assets) && open) {
      const deserializedAssets = assets.map(asset => deserialize(asset));
      setAssetDatas(deserializedAssets);
      setFormState(verifyCommonAttributes(deserializedAssets));
      setIsFormChanged(false);
    }
  }, [open]);

  useEffect(() => {
    if (clear) {
      clearForm();
    }
  }, [clear]);

  function clearForm() {
    setFormState({}, true);
    setIsFormChanged(false);
  };

  const debounceFieldChange = debounce((target: TargetType) => {
    const {name, value, required = false} = target;
    setFormState({[name]: {name, value, required}});
    setIsFormChanged(true);
  }, 400);

  function handleFieldChange(e: ChangeEvent<HTMLInputElement>) {
    e.persist();
    debounceFieldChange(e.target);
  };

  function handleSwitchChange({target}) {
    const {name, checked, required = false} = target;
    setFormState({[name]: {name, value: checked, required}});
    setIsFormChanged(true);
  };

  function handleChooserChange({name, value, required = false}) {
    setFormState({[name]: {name, value: isValue(value, 'none') ? null : value, required}});
    setIsFormChanged(true);
  };

  function handleDateChange({name, value, required = false}) {
    setFormState({[name]: {name, value: toISOString(value), required}});
    setIsFormChanged(true);
  };

  function handleSubmit(e: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    const assetSerializeDatas = assetDatas?.map(asset => {
      const newAsset = {...asset, ...formState};
      return serialize(newAsset);
    });
    onSave?.(assetSerializeDatas);
  }

  const onClose = function() {
    onRemove(onCancel);
  };

  return (
    <EditorDialog open={isVisible}
      dialogTitle={translate('common:editors.bulkEdit.title', {
        count  : assets?.length,
        entity : entityLabel
      })}
      onCloseDialog={onClose}
      onCancelDialog={onClose}
      onPrimaryButtonClick={handleSubmit}
      primaryButtonDisabled={!isFormChanged}
      primaryButtonLoading={loading}>
      <form className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.formWrapper}>
          <FormSection bottomBorder
            title={translate('admin-assets:form.sections.general')}>
            <FormGroupFields>
              <FormItem>
                <GroupChooser account={account}
                  onLoad={setGroupsLoading}
                  readOnly={isMultiDevice}
                  useDisabledStyles={isMultiDevice}
                  onChange={function(value) {
                    setGroupValue(value);
                    handleChooserChange({
                      name : AssetBulkEditorFormFields.Groups,
                      value
                    });
                  }}
                  hasError={formState[AssetBulkEditorFormFields.Groups]?.error}
                  defaultValue={formState[AssetBulkEditorFormFields.Groups]?.value} />
              </FormItem>
              {!isCtcAdmin && <FormItem>
                <EntityTypeChooser entity={GroupTypeEntity.asset}
                  isLoading={groupsLoading}
                  onChange={function(value) {
                    handleChooserChange({
                      name : AssetBulkEditorFormFields.AssetType,
                      value
                    });
                  }}
                  hasError={formState[AssetBulkEditorFormFields.AssetType]?.error}
                  defaultValue={formState[AssetBulkEditorFormFields.AssetType]?.value} />
              </FormItem>}
              {isCtcAdmin && (
                <FormItem>
                  <Dropdown displaySelectedLabel
                    options={getADMAssetTypes()}
                    freeSolo
                    fieldLabel={translate('common:editors.entityType.label', {name: translate('common:entityLabels.asset')})}
                    placeholder={translate('common:options.none')}
                    value={formState[AssetBulkEditorFormFields.AssetType]?.value}
                    name={AssetBulkEditorFormFields.AssetType}
                    autoSelect
                    onChange={function(event, value) {
                      handleChooserChange({
                        name  : AssetBulkEditorFormFields.AssetType,
                        value : value
                      });
                    }} />
                </FormItem>
              )}
              {!isCtcAdmin && (
                <FormItem>
                  <EntityCategoryChooser entity={GroupTypeEntity.asset}
                    isLoading={groupsLoading}
                    onChange={function(value) {
                      handleChooserChange({
                        name : AssetBulkEditorFormFields.AssetCategory,
                        value
                      });
                    }}
                    hasError={formState[AssetBulkEditorFormFields.AssetCategory]?.error}
                    defaultValue={formState[AssetBulkEditorFormFields.AssetCategory]?.value} />
                </FormItem>
              )}
              {isCtcAdmin && (
                <FormItem>
                  <FormTextField fieldLabel={translate('common:editors.entityCategory.label', {name: translate('common:entityLabels.asset')})}
                    placeholder={translate('common:editors.entityCategory.placeholder', {name: translate('common:entityLabels.asset')})}
                    name={AssetBulkEditorFormFields.AssetCategory}
                    onChange={handleFieldChange}
                    hasError={formState[AssetBulkEditorFormFields.AssetCategory]?.error}
                    defaultValue={formState[AssetBulkEditorFormFields.AssetCategory]?.value}
                    maxLength={64} />
                </FormItem>)
              }
              <FormItem>
                <EngineTypeChooser onChange={function(value) {
                  handleChooserChange({
                    name : AssetBulkEditorFormFields.FuelType,
                    value
                  });
                }}
                defaultValue={formState[AssetBulkEditorFormFields.FuelType]?.value} />
              </FormItem>
              <FormItem>
                <EntityStatusChooser entity={Entity.asset}
                  readOnly={!hasEditStatusAccess}
                  onChange={function(value) {
                    handleChooserChange({
                      name : AssetBulkEditorFormFields.Status,
                      value
                    });
                  }}
                  status={formState[AssetBulkEditorFormFields.Status]?.value} />
              </FormItem>
              {!isCtcAdmin && (
                <>
                  <FormItem>
                    <ScheduleChooser accountId={accountId}
                      onChange={function(value) {
                        handleChooserChange({
                          name : AssetBulkEditorFormFields.Schedule,
                          value
                        });
                      } }
                      validationError={formState[AssetBulkEditorFormFields.Schedule]?.error}
                      defaultValue={formState[AssetBulkEditorFormFields.Schedule]?.value} />
                  </FormItem>
                  <FormItem>
                    <MaintenanceScheduleChooser accountId={accountId}
                      onChange={function(value) {
                        handleChooserChange({
                          name : AssetBulkEditorFormFields.MaintenanceSchedule,
                          value
                        });
                      } }
                      validationError={formState[AssetBulkEditorFormFields.MaintenanceSchedule]?.error}
                      defaultValue={formState[AssetBulkEditorFormFields.MaintenanceSchedule]?.value}
                      assetType={formState[AssetBulkEditorFormFields.AssetType]?.value}
                      assetCategory={formState[AssetBulkEditorFormFields.AssetCategory]?.value} />
                  </FormItem>
                </>
              )}
            </FormGroupFields>
          </FormSection>
          <FormSection bottomBorder
            title={translate('admin-assets:form.sections.device')}>
            <FormGroupFields>
              {!isCtcAdmin && (
                <FormItem>
                  <IOProfileChooser accountId={accountId}
                    onChange={function(value) {
                      handleChooserChange({
                        name : AssetBulkEditorFormFields.IoProfile,
                        value
                      });
                    }}
                    validationError={formState[AssetBulkEditorFormFields.IoProfile]?.error}
                    defaultIOProfile={formState[AssetBulkEditorFormFields.IoProfile]?.value} />
                </FormItem>
              )}
              <FormItem className={classes.datePickerItem}>
                <DatePicker closeOnClickOutside
                  useDatePickerTextField
                  isDatePicker={allowManualDateEntry}
                  manualInput={allowManualDateEntry}
                  allowManualEntry={allowManualDateEntry}
                  validDateTimeFormats={validDateFormats}
                  fieldLabel={translate('common:editors.installDate.label')}
                  placeholder={translate('common:editors.shared.selectDate')}
                  open={openDatePickers[AssetBulkEditorFormFields.InstallDate]}
                  toggle={function() {
                    setOpenDatePickers(
                      {
                        [AssetBulkEditorFormFields.InstallDate] :
                          !openDatePickers[AssetBulkEditorFormFields.InstallDate]
                      }
                    );
                  }}
                  onChange={function(value, required) {
                    handleDateChange({
                      name : AssetBulkEditorFormFields.InstallDate,
                      value,
                      required
                    });
                  }}
                  defaultValue={formState[AssetBulkEditorFormFields.InstallDate]?.value} />
              </FormItem>
              <FormItem className={classes.datePickerItem}>
                <DatePicker closeOnClickOutside
                  useDatePickerTextField
                  manualInput={allowManualDateEntry}
                  isDatePicker={allowManualDateEntry}
                  allowManualEntry={allowManualDateEntry}
                  validDateTimeFormats={validDateFormats}
                  fieldLabel={translate('common:editors.uninstallDate.label')}
                  placeholder={translate('common:editors.shared.selectDate')}
                  open={openDatePickers[AssetBulkEditorFormFields.UninstallDate]}
                  toggle={function() {
                    setOpenDatePickers(
                      {
                        [AssetBulkEditorFormFields.UninstallDate] :
                          !openDatePickers[AssetBulkEditorFormFields.UninstallDate]
                      }
                    );
                  }}
                  onChange={function(value, required) {
                    handleDateChange({
                      name : AssetBulkEditorFormFields.UninstallDate,
                      value,
                      required
                    });
                  }}
                  defaultValue={formState[AssetBulkEditorFormFields.UninstallDate]?.value} />
              </FormItem>
            </FormGroupFields>
          </FormSection>
          <FormSection title={translate('admin-assets:form.sections.details')}>
            <FormGroupFields>
              <FormItem>
                <FormTextField fieldLabel={translate('common:editors.make.label')}
                  name={AssetBulkEditorFormFields.Make}
                  onChange={handleFieldChange}
                  hasError={formState[AssetBulkEditorFormFields.Make]?.error}
                  defaultValue={formState[AssetBulkEditorFormFields.Make]?.value} />
              </FormItem>
              <FormItem>
                <FormTextField fieldLabel={translate('common:editors.model.label')}
                  name={AssetBulkEditorFormFields.Model}
                  onChange={handleFieldChange}
                  hasError={formState[AssetBulkEditorFormFields.Model]?.error}
                  defaultValue={formState[AssetBulkEditorFormFields.Model]?.value} />
              </FormItem>
              <FormItem>
                <FormTextField fieldLabel={translate('common:editors.year.label')}
                  name={AssetBulkEditorFormFields.Year}
                  onChange={handleFieldChange}
                  hasError={formState[AssetBulkEditorFormFields.Year]?.error}
                  defaultValue={formState[AssetBulkEditorFormFields.Year]?.value} />
              </FormItem>
              {!isCtcAdmin && (
                <>
                  <FormItem>
                    <FormTextField fieldLabel={translate('common:editors.licenseState.label')}
                      name={AssetBulkEditorFormFields.LicenseState}
                      onChange={handleFieldChange}
                      hasError={formState[AssetBulkEditorFormFields.LicenseState]?.error}
                      defaultValue={formState[AssetBulkEditorFormFields.LicenseState]?.value} />
                  </FormItem>
                  <FormItem>
                    <FormTextField fieldLabel={translate('common:editors.fuelTankCapacity.label')}
                      name={AssetBulkEditorFormFields.FuelTankCapacity}
                      onChange={handleFieldChange}
                      hasError={formState[AssetBulkEditorFormFields.FuelTankCapacity]?.error}
                      defaultValue={formState[AssetBulkEditorFormFields.FuelTankCapacity]?.value}
                      type='number' />
                  </FormItem>
                  <FormItem>
                    <FormTextField fieldLabel={translate('common:editors.fuelBurnRate.label')}
                      name={AssetBulkEditorFormFields.FuelBurnRate}
                      onChange={handleFieldChange}
                      hasError={formState[AssetBulkEditorFormFields.FuelBurnRate]?.error}
                      defaultValue={formState[AssetBulkEditorFormFields.FuelBurnRate]?.value} />
                  </FormItem></>
              )}
            </FormGroupFields>
          </FormSection>
          {!isCtcAdmin && (
            <div className={classes.overrides}>
              <Typography className={classes.overridesTitle}>
                {translate('admin-assets:form.sections.overrideBusValue')}
              </Typography>
              <div className={classes.overridesSwitches}>
                <Switch label={translate('common:editors.odometer.label')}
                  marginRight={isPhone ? '25px' : '52px'}
                  name={AssetBulkEditorFormFields.OverrideOdometer}
                  onChange={handleSwitchChange}
                  checked={formState[AssetBulkEditorFormFields.OverrideOdometer]?.value} />
                <Switch label={translate('common:editors.engineHours.label')}
                  marginRight={isPhone ? '25px' : '52px'}
                  name={AssetBulkEditorFormFields.OverrideEngineRunTime}
                  onChange={handleSwitchChange}
                  checked={formState[AssetBulkEditorFormFields.OverrideEngineRunTime]?.value} />
                <Switch label={translate('common:editors.speed.label')}
                  marginRight={isPhone ? '25px' : '52px'}
                  name={AssetBulkEditorFormFields.OverrideSpeed}
                  onChange={handleSwitchChange}
                  checked={formState[AssetBulkEditorFormFields.OverrideSpeed]?.value} />
              </div>
            </div>
          )}
        </div>
      </form>
    </EditorDialog>
  );
};

export default AssetBulkEditor;
